import { useCallback, useContext } from 'react';

import canvasContentTypeCheckers from 'types/cardTypeCheckers';
import isDefined from 'isDefined';
import CurrentUserContext from 'contexts/CurrentUserContext';
import STORE from 'store';
import AccountPickerContext from 'contexts/AccountPickerContext';
import getTimeStamp from 'getTimeStamp';
import DashboardGadgetsContext from 'contexts/DashboardGadgetsContext';
import MetricListsContext from 'contexts/MetricListsContext';
import getIdentifier from 'getIdentifier';

import useCopyVisHelpers from './useCopyVisHelpers';
import useToPlatesWithoutVariables from './usePlatesWithoutVariables';

const useCreateDashboardCopy = () => {
  const currentUser = useContext(CurrentUserContext);
  const { dashboardGadgetsLookup } = useContext(DashboardGadgetsContext);
  const { metricListsLookup } = useContext(MetricListsContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { generateNewVisDef, getMetricIdForNewVis } = useCopyVisHelpers();
  const toPlatesWithoutVariables = useToPlatesWithoutVariables();

  const copyDashboardGadget = useCallback(
    async ({
      c,
      dashboard,
    }: {
      c: CanvasCard.DashboardGadgetCard;
      dashboard: DashboardType;
    }): Promise<CanvasCard.DashboardGadgetCard | undefined> => {
      const existingDashboardGadget =
        dashboardGadgetsLookup[c.content.dashboardGadgetId];
      if (!existingDashboardGadget) {
        return undefined;
      }
      const newChartDef = await generateNewVisDef(
        existingDashboardGadget.chartDef,
      );
      const newDashboardGadget = {
        ...existingDashboardGadget,
        chartDef: newChartDef,
        id: getIdentifier(),
        drillDowns: toPlatesWithoutVariables({
          plates: existingDashboardGadget.drillDowns || window.emptyArray,
          variableDrillDowns: dashboard.variableDrillDowns || window.emptyArray,
        }),
        scope: toPlatesWithoutVariables({
          plates: existingDashboardGadget.scope || window.emptyArray,
          variableDrillDowns: dashboard.variableDrillDowns || window.emptyArray,
        }),
        createdBy: currentUser.id,
        createdOn: getTimeStamp(),
        updatedBy: currentUser.id,
        updatedOn: getTimeStamp(),
      };

      await STORE.visualisations
        .getDashboardGadgetsRef({ accountId: selectedAccountId })
        .doc(newDashboardGadget.id)
        .set(newDashboardGadget);

      return {
        ...c,
        content: {
          ...c.content,
          dashboardGadgetId: newDashboardGadget.id,
        },
      };
    },
    [
      currentUser.id,
      dashboardGadgetsLookup,
      generateNewVisDef,
      selectedAccountId,
      toPlatesWithoutVariables,
    ],
  );

  const copyMetricList = useCallback(
    async ({
      c,
      dashboard,
    }: {
      c: CanvasCard.MetricListCard;
      dashboard: DashboardType;
    }): Promise<CanvasCard.MetricListCard | undefined> => {
      const existingMetricList = metricListsLookup[c.content.metricListId];
      if (!existingMetricList) {
        return;
      }
      const newMetricList: MetricListGadgetType = {
        ...existingMetricList,
        id: getIdentifier(),
        createdBy: currentUser.id,
        createdOn: getTimeStamp(),
        updatedBy: currentUser.id,
        updatedOn: getTimeStamp(),
      };

      const newList: MetricListItemType[] = [];

      // Create new copies of the saved filters which metric list items
      // require. This ensures changes are made in isolation
      const newSavedFilterRequests = newMetricList.list.map(async (i) => {
        if (i.savedFilterId) {
          const currentSavedFilter = await STORE.filterDefinitions
            .getLegacySavedFiltersRef({ accountId: selectedAccountId })
            .doc(i.savedFilterId)
            .get()
            .then((doc) => doc.data());
          if (!currentSavedFilter) {
            const e = new Error();
            e.name = 'LegacySavedFilter not found';
            throw e;
          }
          const newSavedFilter = {
            ...currentSavedFilter,
            id: getIdentifier(),
            drillDowns: toPlatesWithoutVariables({
              plates: currentSavedFilter.drillDowns,
              variableDrillDowns: dashboard.variableDrillDowns,
            }),
            scope: toPlatesWithoutVariables({
              plates: currentSavedFilter.scope,
              variableDrillDowns: dashboard.variableDrillDowns,
            }),
            createdBy: currentUser.id,
            createdOn: getTimeStamp(),
            updatedBy: currentUser.id,
            updatedOn: getTimeStamp(),
          };

          await STORE.filterDefinitions
            .getLegacySavedFiltersRef({ accountId: selectedAccountId })
            .doc(newSavedFilter.id)
            .set(newSavedFilter);
          const newI: MetricListItemType = {
            ...i,
            metricId: await getMetricIdForNewVis(i.metricId),
            savedFilterId: newSavedFilter.id,
          };

          newList.push(newI);
        }
      });
      await Promise.all(newSavedFilterRequests);

      newMetricList.list = newList;
      await STORE.visualisations
        .getMetricListsRef({
          accountId: selectedAccountId,
        })
        .doc(newMetricList.id)
        .set(newMetricList);

      return {
        ...c,
        content: {
          ...c.content,
          metricListId: newMetricList.id,
        },
      };
    },
    [
      currentUser.id,
      getMetricIdForNewVis,
      metricListsLookup,
      selectedAccountId,
      toPlatesWithoutVariables,
    ],
  );

  const copyCanvas = useCallback(
    async ({
      dashboard,
      templatedFrom,
    }: {
      dashboard: DashboardType;
      templatedFrom?: DashboardType;
    }): Promise<Canvas> => {
      const canvas = templatedFrom ? templatedFrom.canvas : dashboard.canvas;
      const cardRequests = canvas.cards.map((c) => {
        if (canvasContentTypeCheckers.isDashboardGadget(c)) {
          return copyDashboardGadget({ c, dashboard });
        } else if (canvasContentTypeCheckers.isMetricList(c)) {
          return copyMetricList({ c, dashboard });
        } else {
          return Promise.resolve({
            ...c,
          });
        }
      });
      const newCards = await Promise.all(cardRequests);
      const newCanvas = {
        ...canvas,
        cards: newCards.filter(isDefined),
      };

      return newCanvas;
    },
    [copyDashboardGadget, copyMetricList],
  );

  const createDashboardCopy = useCallback(
    async ({
      name,
      dashboard,
      isCreatingTemplate,
      templatedFrom,
      access,
      wallboard,
      labelIds,
    }: {
      name: string;
      dashboard: DashboardType;
      isCreatingTemplate?: boolean;
      templatedFrom?: DashboardType;
      access: ResourceAccess;
      wallboard?: Wallboard;
      labelIds?: string[];
    }): Promise<{
      newDashboard: DashboardType;
      newDashboardId: string;
    }> => {
      const newCanvas = await copyCanvas({
        dashboard,
        templatedFrom,
      });

      const newDashboard = {
        ...dashboard,
        id: getIdentifier(),
        name,
        canvas: newCanvas,
        variableDrillDowns: [],
        isTemplate: !!isCreatingTemplate,
        templateId: undefined,
        isEntityDetailsTab: false,
        access,
        ...(labelIds !== undefined && { labelIds }),
        createdBy: currentUser.id,
        createdOn: getTimeStamp(),
        updatedBy: currentUser.id,
        updatedOn: getTimeStamp(),
      };

      if (wallboard) {
        newDashboard.isWallboardSlide = true;
        newDashboard.wallboardId = wallboard.id;
      }

      await STORE.getDashboardsRef({
        accountId: selectedAccountId,
      })
        .doc(newDashboard.id)
        .set(newDashboard);

      return {
        newDashboardId: newDashboard.id,
        newDashboard,
      };
    },
    [copyCanvas, currentUser.id, selectedAccountId],
  );

  return createDashboardCopy;
};

export default useCreateDashboardCopy;
